import { useEffect, useState } from "react";
import apiClient from "@/services/api/apiClient";

/**
 * Resolve an image source that may point at our own API.
 *
 * Medication photos are stored as `/api/fhir/medications/:id/images/...`.
 * That route used to be public, so a plain `<img src>` worked - and so did
 * counting through the ids to download everyone's photos. It now requires the
 * bearer token, which an `<img>` element cannot send, so API paths are fetched
 * through the API client and shown as an object URL.
 *
 * Anything else (data: URLs from a fresh camera capture, absolute URLs) is
 * returned unchanged.
 */
export function useAuthenticatedImage(
  src: string | null | undefined,
): string | null {
  const isApiPath = typeof src === "string" && src.startsWith("/api/");
  const [resolved, setResolved] = useState<string | null>(
    isApiPath ? null : (src ?? null),
  );

  useEffect(() => {
    if (!src || !src.startsWith("/api/")) {
      setResolved(src ?? null);
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;
    setResolved(null);

    apiClient
      .get<Blob>(src.slice("/api".length), { responseType: "blob" })
      .then((response) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(response.data);
        setResolved(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setResolved(null);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  return resolved;
}
